import { useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useGrcAuth } from './useGrcAuth.js';
import GrcLogin, { MfaSetupScreen } from './GrcLogin.jsx';
import StepUpDialog from '../components/auth/StepUpDialog.jsx';
import GrcClientList from './GrcClientList.jsx';
import GrcClientDetail from './GrcClientDetail.jsx';
import GrcEngagementDetail from './GrcEngagementDetail.jsx';
import GrcFirmSettings from './GrcFirmSettings.jsx';
import AppLogo from '../components/shared/AppLogo.jsx';
import { AURORA_LIGHT_VARS } from '../layouts/aurora/palettes.js';
import { readGrcLayout, stepUpVerify, mfaEnrollStart } from './grcClient.js';

function GrcShell({ session, onLogout, children }) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top bar */}
      <div
        style={{
          height: 52,
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 24px',
          background: 'var(--bg-topbar, var(--bg2))',
          flexShrink: 0,
          gap: 12,
        }}
      >
        {/* Logo + name */}
        <div
          onClick={() => navigate('/')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <AppLogo size={28} borderRadius={7} />
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: 'var(--fg)',
              fontFamily: "'Syne', 'Roboto Slab', sans-serif",
              lineHeight: 1,
            }}
          >
            Tryggr <span style={{ color: 'var(--accent)' }}>GRC</span>
          </div>
        </div>

        {/* Nav */}
        <div style={{ display: 'flex', gap: 2, marginLeft: 20 }}>
          {[
            {
              label: 'Clients',
              path: '/',
              activeWhen: (p) =>
                p === '/' || p.startsWith('/clients') || p.startsWith('/engagements'),
            },
            ...(session?.role === 'firm-admin'
              ? [{ label: 'Team', path: '/settings', activeWhen: (p) => p.startsWith('/settings') }]
              : []),
          ].map((item) => {
            const active = item.activeWhen(location.pathname);
            return (
              <button
                key={item.path}
                className="btn btn-sm"
                onClick={() => navigate(item.path)}
                style={{
                  background: active ? 'var(--accent-bg)' : 'transparent',
                  color: active ? 'var(--accent)' : 'var(--fg3)',
                  borderColor: 'transparent',
                  fontWeight: active ? 600 : 400,
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {/* User + logout */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--fg3)' }}>
            {session?.displayName || session?.email}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={onLogout}>
            Sign out
          </button>
        </div>
      </div>

      {/* Page content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>{children}</div>
    </div>
  );
}

function GrcRoutes({ session, logout, completeLogin }) {
  // §16 (FDNA-02 D2): the shared re-verify dialog, pointed at the auditor
  // identity — auditor-step-up-verify, auditor-mfa-enroll and the auditor's
  // own enrolment screen. Gated bulk exports open it via grcClient.request().
  const stepUpApi = useMemo(
    () => ({
      begin: () => stepUpVerify('begin'),
      verify: (body) => stepUpVerify('verify', body),
      enrolStart: (password) => mfaEnrollStart(password),
      adoptSession: (token, user) => completeLogin(token, user),
      renderEnrolSetup: ({ challengeToken, onDone, onCancel }) => (
        <MfaSetupScreen challengeToken={challengeToken} onSuccess={onDone} onBack={onCancel} />
      ),
      recoveryHint:
        'Your authenticator cannot be used. Ask your firm administrator to reset it, then try again.',
    }),
    [completeLogin]
  );
  return (
    <GrcShell session={session} onLogout={logout}>
      <StepUpDialog api={stepUpApi} />
      <Routes>
        <Route path="/" element={<GrcClientList session={session} />} />
        <Route path="/clients/:clientSlug" element={<GrcClientDetail session={session} />} />
        <Route path="/engagements/:id" element={<GrcEngagementDetail />} />
        {session?.role === 'firm-admin' && (
          <Route path="/settings" element={<GrcFirmSettings session={session} />} />
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </GrcShell>
  );
}

export default function GrcAuditorApp() {
  const { session, loading, login, completeLogin, logout } = useGrcAuth();

  // Apply theme palette — the GRC portal renders before the main app's useTheme
  // hook, so we set CSS vars directly on mount. Default to Aurora Dark (v2);
  // only skip if the registry explicitly stores 'beta' for the GRC portal.
  useEffect(() => {
    readGrcLayout().then((layoutId) => {
      if (layoutId !== 'beta') {
        const root = document.documentElement;
        Object.entries(AURORA_LIGHT_VARS).forEach(([prop, val]) =>
          root.style.setProperty(prop, val)
        );
      }
    });
  }, []);

  if (loading) return <div style={{ minHeight: '100vh', background: 'var(--bg)' }} />;
  if (!session) {
    // Attach completeLogin as a property so GrcLogin can call it after MFA.
    // This avoids prop-drilling another separate prop through GrcLogin's interface.
    const loginWithComplete = Object.assign(login, { __complete: completeLogin });
    return <GrcLogin onLogin={loginWithComplete} />;
  }

  return <GrcRoutes session={session} logout={logout} completeLogin={completeLogin} />;
}
